/**
 * Accessibility projection for boolean field widgets (checkbox / switch).
 */
import { submitFalsePart } from "../submission.js";
import { MDY_SUBMIT_FALSE_CLASS } from "../structure.js";
import { fieldDescribedBy } from "./shell-a11y.js";
import { assertUsableWidgetId } from "../ids.js";
import { MDY_FIELD_SHELL_CLASSES } from "../structure.js";
import { errorsVisible, holdsUneditedValue, shownErrors } from "./verdict.js";
import { fieldShellRootClasses } from "./shell-a11y.js";
/** Builds the static IDs used by a boolean field widget view. */
export function booleanFieldPartIds(widgetId) {
    assertUsableWidgetId(widgetId);
    return {
        inputId: `${widgetId}__input`,
        labelId: `${widgetId}__label`,
        descriptionId: `${widgetId}__description`,
        errorId: `${widgetId}__errors`,
    };
}
/**
 * Computes the public state classes for the boolean field root.
 *
 * Through the shared vocabulary rather than as literals: these were nine hand-written `mdy-field--*`
 * names, and no theme styled a single one of them.
 */
/** The root's classes, from the shared table every kind reads. */
export function booleanFieldRootClasses(state) {
    return fieldShellRootClasses(state);
}
/** Projects ARIA attributes and classes for the boolean field parts. */
export function projectBooleanFieldA11y(state, errors, options) {
    const { inputId, labelId, descriptionId, errorId } = booleanFieldPartIds(options.widgetId);
    // Out of play, no verdict — the wrapper, the label, `aria-invalid` and whether the error
    // text renders are four faces of one question, answered once in verdict.ts.
    const hasErrors = shownErrors(state, errors).length > 0;
    // Whether the person is being told yet — `aria-invalid` says the same thing the error list does.
    // A rule they have not answered waits for them to reach the field; a refusal about the value
    // already there does not, because they can neither cause it by inaction nor see the reason unless
    // it is said.
    const tellingThem = errorsVisible({ disabled: state.disabled, touched: state.touched, holdsUnedited: holdsUneditedValue(state) }, errors);
    // Both, error first — an error does not take the place of the instruction that would have
    // prevented it. The container is pointed at while it is on the page, which is not the same as
    // while it holds a message: a renderer that reserves it keeps one reference that never changes.
    const describedBy = fieldDescribedBy({
        errorId,
        descriptionId,
        errorsPresent: options.errorsReserved ?? hasErrors,
        // A description exists when the renderer says one does. Defaulted to `true`, a control claimed
        // `aria-describedby` at rest and pointed it at an element holding nothing — which asserts that a
        // description exists and sends a reader to a text nobody wrote. It also makes "I have a
        // description, and it is empty" indistinguishable from "I have none", where silence is the
        // honest statement of nothing to say. The errors half of this reference was repaired for exactly
        // that reason; the hint half is the same shape.
        descriptionPresent: options.supportingText === true || (options.supportingText ?? "") !== "",
    });
    const isSwitch = options.variant === "switch";
    return {
        root: {
            classes: booleanFieldRootClasses(state),
            attributes: {},
        },
        label: {
            id: labelId,
            // A switch labels its text `mdy-toggle__label`; `mdy-checkbox`/`mdy-toggle` belong on the
            // wrapping element, which is why the input itself carries no class of its own.
            classes: [isSwitch ? "mdy-toggle__label" : MDY_FIELD_SHELL_CLASSES.label],
            // The clickable element is the wrapper, and the text sits inside it as a span — a `for`
            // here would be an invalid attribute on a non-label element.
            attributes: {},
        },
        indicator: {
            // A switch draws track and thumb instead; the checkbox's box is one element.
            classes: isSwitch ? ["mdy-toggle__track"] : ["mdy-checkbox__indicator"],
            attributes: { "aria-hidden": "true" },
        },
        requiredMarker: {
            classes: [MDY_FIELD_SHELL_CLASSES.requiredMarker],
            attributes: { "aria-hidden": "true" },
        },
        // Carries `false` under the field's key, **after** the box in the document: a hidden input
        // before the visible control changes what `querySelector("input")` and `.first()` mean for
        // everyone reading the field, and that is the most obvious selector anybody writes.
        submitFalse: options.submitName === undefined
            // No key to submit under, so the element carries nothing but the class that names it. It is
            // still the part: a renderer draws it either way, and a part findable in only one of its two
            // states is findable by nobody writing a check.
            ? { classes: [MDY_SUBMIT_FALSE_CLASS], attributes: {} }
            // Asked of the projection that owns this element rather than restated here. Both spellings
            // existed, with the same attributes and the same paragraph of reasoning beside each, and two
            // renderers read one while the third read the other.
            : submitFalsePart(options.submitName, { disabled: state.disabled, checked: state.checked }),
        input: {
            id: inputId,
            classes: [],
            attributes: {
                // A switch is a checkbox input carrying `role="switch"`. Dropping the type would leave a
                // text input behind.
                type: "checkbox",
                role: isSwitch ? "switch" : "checkbox",
                checked: state.checked === true,
                // What a native submit reads, and the value it reads rather than the `on` HTML defaults to:
                // a payload saying `on` describes the box, not the answer.
                name: options.submitName ?? null,
                value: options.submitName === undefined ? null : "true",
                // The switch's, and not the box's. `role="switch"` has no host-language state to read, so
                // the attribute is the only thing that says whether it is on. A native `<input
                // type="checkbox">` maps its own `checked` into the accessibility tree, and writing the
                // attribute beside it is a second source for one fact — the two can disagree, and when they
                // do the ARIA one wins and can be the stale one. Renderers already disagreed about it here,
                // which is what a redundant attribute invites.
                //
                // One of the tokens the attribute is allowed to hold, never `String(whatever arrived)`.
                // This projection is published, so the state is the caller's to supply and its shape is not
                // ours to guarantee — but the attribute's value is, and `aria-checked="undefined"` maps to
                // nothing in any assistive technology, on the single attribute that says whether a switch is
                // on. `mixed` is not produced: `MdyBooleanFieldState.checked` is a boolean and no field in
                // the engine has an indeterminate value, so emitting a third token would describe a state
                // nothing can be in.
                //
                // `null` for the box rather than the key left out: this contract says "no attribute" with
                // `null` everywhere else — `aria-readonly` beside it does — and a key that is simply absent
                // reads as `undefined` to anything asking the object what it says, which is a value no
                // reader maps and outside the three the standard allows.
                "aria-checked": options.variant === "switch"
                    ? (state.checked === true ? "true" : "false")
                    : null,
                "aria-invalid": String(tellingThem),
                "aria-required": String(state.required),
                // `aria-disabled` reflects `disabled` alone. A read-only control is not disabled: it takes
                // focus, its text can be selected and copied, and announcing it as disabled tells a
                // screen-reader user they cannot interact with something they can. `aria-readonly`
                // carries read-only, and only on the kinds that declare the state.
                "aria-disabled": String(state.disabled),
                "aria-describedby": describedBy,
                // ARIA only, never the native attribute: HTML ignores `readonly` on a checkbox, so binding
                // it binds nothing. What refuses the toggle is the controller, and this is what says so.
                // A read-only field refuses the change and stays in play: focusable, submitted, validated.
                // The controller has held that rule for every kind for as long as `blocksValueChange` has
                // existed; saying nothing about it left a control that refuses with no way to say why.
                "aria-readonly": state.readonly ? "true" : null,
                disabled: state.disabled,
            },
        },
        description: {
            id: descriptionId,
            classes: [MDY_FIELD_SHELL_CLASSES.supportingText],
            attributes: {},
            ...(typeof options.supportingText === "string" && options.supportingText !== ""
                ? { content: { text: options.supportingText } } : {}),
        },
        error: {
            id: errorId,
            classes: [MDY_FIELD_SHELL_CLASSES.errors],
            attributes: {
                // A live region and nothing more. `role="alert"` here would override the list semantics of
                // the <ul> it sits on: axe reports every <li> inside such a list as an orphaned list item, and
                // a screen reader sees the same thing. `aria-live` announces the list when it appears, so the
                // role would cost the structure and add nothing.
                "aria-live": "polite",
            },
        },
    };
}
